import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './Login.css';

interface LoginForm {
  email: string;
  password: string;
}


const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // Validate token
      apiService.validateToken()
        .then(() => {
          // User is already logged in, redirect
          const from = location.state?.from?.pathname || '/upload';
          navigate(from, { replace: true });
        })
        .catch(() => {
          // Token invalid, clear it
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');
        });
    }
  }, [navigate, location]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim() || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.login(formData.email, formData.password);
      
      // Store tokens and user data
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user_data', JSON.stringify(response.user));

      // Check if password change is required
      if (response.user.require_password_change) {
        navigate('/change-password', { 
          state: { 
            message: 'Please change your temporary password before continuing',
            user: response.user 
          }
        });
        return;
      }

      // Successful login - redirect to intended page or upload
      const from = location.state?.from?.pathname || '/upload';
      navigate(from, { replace: true });

    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.response?.status === 423) {
        setError('Account temporarily locked due to too many failed attempts. Please try again later.');
      } else if (error.response?.status === 401) {
        setError('Invalid email or password');
      } else {
        setError(error.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Welcome to Proven</h1>
          <p>Sign in to your account to access EUDR compliance analysis</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              required
              autoComplete="email"
              className={error ? 'error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                className={error ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁' : '●'}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon"><strong>⚠</strong></span>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !formData.email.trim() || !formData.password}
            className="login-button"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>

          <div className="login-footer">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="forgot-password-link"
            >
              Forgot your password?
            </button>
            
            <div className="signup-link">
              Don't have an account?{' '}
              <Link to="/signup">Create one here</Link>
            </div>
          </div>
        </form>

      </div>
    </div>
  );
};

export default Login;